import { createAction } from "@reduxjs/toolkit";
import _ from "lodash";
import axios from "axios";
import { takeEvery, call, put, select, take } from "redux-saga/effects";
import buildUrl from "build-url-ts";
import { SSE } from "sse.js";
import {
  AddContentToCourseRequest,
  Course,
  CreateAssessmentRequest,
  CreateCourseErrorResponse,
  CreateCourseRequest,
  CreateCourseResponse,
  CreateQuestionInAssessmentRequest,
  DeleteCourseErrorResponse,
  DeleteCourseResponse,
  DeleteQuestionInAssessmentRequest,
  DiscardCourseDraftErrorResponse,
  DiscardCourseDraftResponse,
  FetchCourseErrorResponse,
  FetchCourseResponse,
  FetchDraftCoursesErrorResponse,
  FetchDraftCoursesRequest,
  FetchDraftCoursesResponse,
  FetchPublishedCoursesErrorResponse,
  FetchPublishedCoursesRequest,
  FetchPublishedCoursesResponse,
  PublishCourseErrorResponse,
  PublishCourseResponse,
  RearrangeEntitiesInCourseRequest,
  RearrangeQuestionsInAssessmentRequest,
  RemoveEntityFromCourseRequest,
  UpdateAssessmentPropertyRequest,
  UpdateCourseErrorResponse,
  UpdateCoursePropertyRequest,
  UpdateCourseRequest,
  UpdateCourseResponse,
  UpdateLessonPropertyRequest,
  UpdateQuestionInAssessmentRequest,
  UpdateValidationErrorsForCourseRequest,
  GenerateQuestionsRequest,
  GenerateQuestionsResponse,
  GenerateQuestionsErrorResponse,
  FetchLessonForCourseRequest,
  FetchLessonForCourseResponse,
  FetchLessonForCourseErrorResponse,
  FetchLessonPreviewForCourseRequest,
  FetchLessonPreviewForCourseErrorResponse,
  FetchLessonPreviewForCourseResponse,
  SearchCoursesRequest,
  SearchCoursesResponse,
  SearchCoursesErrorResponse,
  CreateCourseSectionRequest,
  UpdateCourseSectionPropertyRequest,
  FetchAttemptsForCourseRequest,
  FetchAttemptsForCourseResponse,
  FetchAttemptsForCourseErrorResponse,
  FetchCourseLearningRecordResponse,
  FetchCourseLearningRecordErrorResponse,
  FetchLearnersAssessmentReportRequest,
  FetchLearnersAssessmentReportResponse,
  FetchLearnersAssessmentReportErrorResponse,
  SearchPublishedSimulationsRequest,
  SearchPublishedSimulationsResponse,
  SearchPublishedSimulationsErrorResponse,
  SearchPublishedPodcastsRequest,
  SearchPublishedPodcastsErrorResponse,
  SearchPublishedPodcastsResponse,
} from "src/types/coaching/courses";

import {
  ADD_CONTENT_TO_COURSE,
  CREATE_ASSESSMENT_REQUEST,
  CREATE_COURSE_REQUEST,
  CREATE_QUESTION_IN_ASSESSMENT_REQUEST,
  CREATING_COURSE_FAILED,
  CREATING_COURSE_SUCCEEDED,
  DELETE_COURSE_CONTENT,
  DELETE_COURSE_REQUEST,
  DELETE_QUESTION_IN_ASSESSMENT_REQUEST,
  DELETING_COURSE_FAILED,
  DELETING_COURSE_SUCCEEDED,
  DISCARD_COURSE_DRAFT_REQUEST,
  DISCARDING_COURSE_DRAFT_FAILED,
  DISCARDING_COURSE_DRAFT_SUCCEEDED,
  EDIT_COURSE_REQUEST,
  FETCH_COURSE_REQUEST,
  FETCH_DRAFT_COURSE_REQUEST,
  FETCH_LESSON_FOR_COURSE_REQUEST,
  FETCH_LESSON_PREVIEW_FOR_COURSE_REQUEST,
  FETCHING_COURSE_FAILED,
  FETCHING_COURSE_SUCCEEDED,
  FETCHING_DRAFT_COURSE_FAILED,
  FETCHING_DRAFT_COURSE_SUCCEEDED,
  FETCHING_DRAFT_COURSES_FAILED,
  FETCHING_DRAFT_COURSES_REQUEST,
  FETCHING_DRAFT_COURSES_SUCCEEDED,
  FETCHING_LESSON_FOR_COURSE_FAILED,
  FETCHING_LESSON_FOR_COURSE_SUCCEEDED,
  FETCHING_LESSON_PREVIEW_FOR_COURSE_FAILED,
  FETCHING_LESSON_PREVIEW_FOR_COURSE_SUCCEEDED,
  FETCHING_PUBLISHED_COURSES_FAILED,
  FETCHING_PUBLISHED_COURSES_REQUEST,
  FETCHING_PUBLISHED_COURSES_SUCCEEDED,
  GENERATE_QUESTIONS_REQUEST,
  GENERATING_QUESTIONS_FAILED,
  GENERATING_QUESTIONS_SUCCEEDED,
  INVALIDATE_COURSE_CREATION_STATE,
  INVALIDATE_COURSE_DELETION_STATE,
  INVALIDATE_COURSE_UPDATE_STATE,
  INVALIDATE_COURSES,
  INVALIDATE_GENERATE_QUESTIONS,
  INVALIDATE_PUBLISH_COURSE,
  PUBLISH_COURSE_REQUEST,
  PUBLISHING_COURSE_FAILED,
  PUBLISHING_COURSE_SUCCEEDED,
  REARRANGE_ENTITIES_IN_COURSE,
  REARRANGE_QUESTIONS_IN_ASSESSMENT_REQUEST,
  REMOVE_ENTITY_FROM_COURSE,
  UPDATE_ASSESSMENT_PROPERTY_REQUEST,
  UPDATE_COURSE_PROPERTY_REQUEST,
  UPDATE_LESSON_PROPERTY_REQUEST,
  UPDATE_QUESTION_IN_ASSESSMENT_REQUEST,
  UPDATE_VALIDATION_ERRORS_FOR_COURSE,
  UPDATING_COURSE_FAILED,
  UPDATING_COURSE_REQUEST,
  UPDATING_COURSE_SUCCEEDED,
  VALIDATE_COURSE_REQUEST,
  SEARCH_COURSES_REQUEST,
  SEARCHING_COURSES_SUCCEEDED,
  SEARCHING_COURSES_FAILED,
  CREATE_COURSE_SECTION_REQUEST,
  UPDATE_COURSE_SECTION_PROPERTY_REQUEST,
  SEARCH_COURSES_SSE_SUBSCRIBE,
  SEARCH_COURSES_SSE_MESSAGE_RECEIVED,
  SEARCH_COURSES_SSE_ERROR,
  INVALIDATE_SSE_SEARCH_RESULTS,
  INVALIDATE_DRAFT_COURSE,
  FETCH_ATTEMPTS_FOR_COURSE_REQUEST,
  FETCHING_ATTEMPTS_FOR_COURSE_SUCCEEDED,
  FETCHING_ATTEMPTS_FOR_COURSE_FAILED,
  FETCH_COURSE_LEARNING_REQUEST,
  FETCHING_COURSE_LEARNING_RECORD_SUCCEEDED,
  FETCHING_COURSE_LEARNING_RECORD_FAILED,
  FETCH_LEARNERS_ASSESSMENT_REPORT_REQUEST,
  FETCHING_LEARNERS_ASSESSMENT_REPORT_SUCCEEDED,
  FETCHING_LEARNERS_ASSESSMENT_REPORT_FAILED,
  INVALIDATE_LEARNERS_ASSESSMENT_REPORT,
  FETCH_PUBLISHED_SIMULATIONS_REQUEST,
  FETCHING_PUBLISHED_SIMULATIONS_SUCCEEDED,
  FETCHING_PUBLISHED_SIMULATIONS_FAILED,
  FETCH_PUBLISHED_PODCASTS_REQUEST,
  FETCHING_PUBLISHED_PODCASTS_SUCCEEDED,
  FETCHING_PUBLISHED_PODCASTS_FAILED,
  SEARCH_PUBLISHED_SIMULATIONS_REQUEST,
  SEARCHING_PUBLISHED_SIMULATIONS_SUCCEEDED,
  SEARCHING_PUBLISHED_SIMULATIONS_FAILED,
  SEARCH_PUBLISHED_PODCASTS_REQUEST,
  SEARCHING_PUBLISHED_PODCASTS_SUCCEEDED,
  SEARCHING_PUBLISHED_PODCASTS_FAILED,
  INVALIDATE_PUBLISHED_SIMULATIONS,
  INVALIDATE_PUBLISHED_PODCASTS,
} from "../actionTypes";
import { validateCourseRequest } from "src/pages/coaching/manage/courses/edit/helper";
import { Channel, eventChannel, EventChannel } from "redux-saga";
// import { SSE } from "sse.js";
import { getAccessToken } from "src/utils/authentication.service";

// eslint-disable-next-line no-undef
const apiUrl = `${process.env.REACT_APP_COACHING_API_URL}`;
const searchApiUrl = `${process.env.REACT_APP_COACHING_SEARCH_API_URL}`;


// fetch draft courses
export const fetchDraftCourses = createAction<FetchDraftCoursesRequest>(
  FETCHING_DRAFT_COURSES_REQUEST,
);
const fetchingDraftCoursesSucceeded = createAction<FetchDraftCoursesResponse>(
  FETCHING_DRAFT_COURSES_SUCCEEDED,
);
const fetchingDraftCoursesFailed = createAction<FetchDraftCoursesErrorResponse>(
  FETCHING_DRAFT_COURSES_FAILED,
);

const fetchDraftCoursesSaga = function* (
  action: ReturnType<typeof fetchDraftCourses>,
): any {
  const request = action.payload as FetchDraftCoursesRequest;

  const fetchCoursesApi = () => {
    return axios({
      method: "GET",
      url: `${apiUrl}/api/1.0/courses/drafts`,
      params: request,
    }).then((response) => response.data);
  };

  try {
    const response = yield call(fetchCoursesApi);
    yield put(
      fetchingDraftCoursesSucceeded({
        response: response,
      }),
    );
  } catch (error) {
    yield put(fetchingDraftCoursesFailed({ error: error }));
  }
};

// fetch draft courses
export const fetchPublishedCourses = createAction<FetchPublishedCoursesRequest>(
  FETCHING_PUBLISHED_COURSES_REQUEST,
);
const fetchingPublishedCoursesSucceeded =
  createAction<FetchPublishedCoursesResponse>(
    FETCHING_PUBLISHED_COURSES_SUCCEEDED,
  );
const fetchingPublishedCoursesFailed =
  createAction<FetchPublishedCoursesErrorResponse>(
    FETCHING_PUBLISHED_COURSES_FAILED,
  );

const fetchPublishedCoursesSaga = function* (
  action: ReturnType<typeof fetchPublishedCourses>,
): any {
  const request = action.payload as FetchPublishedCoursesRequest;

  const fetchCoursesApi = () => {
    return axios({
      method: "GET",
      url: `${apiUrl}/api/1.0/courses/published`,
      params: request,
    }).then((response) => response.data);
  };

  try {
    const response = yield call(fetchCoursesApi);
    yield put(
      fetchingPublishedCoursesSucceeded({
        response: response,
      }),
    );
  } catch (error) {
    yield put(fetchingPublishedCoursesFailed({ error: error }));
  }
};

// create course
export const createCourse = createAction<CreateCourseRequest>(
  CREATE_COURSE_REQUEST,
);
const creatingCourseSucceeded = createAction<CreateCourseResponse>(
  CREATING_COURSE_SUCCEEDED,
);
const creatingCourseFailed = createAction<CreateCourseErrorResponse>(
  CREATING_COURSE_FAILED,
);

const createCourseSaga = function* (
  action: ReturnType<typeof createCourse>,
): any {
  const request = action.payload;

  const url = buildUrl(apiUrl, {
    path: `api/1.0/courses`,
  });

  const createCourseApi = () => {
    return axios({
      method: "POST",
      url: url,
      data: request,
    }).then((response) => response.data);
  };

  try {
    const response = yield call(createCourseApi);
    yield put(creatingCourseSucceeded({ response: response }));
  } catch (error) {
    yield put(creatingCourseFailed({ error: error }));
  }
};

// fetch course
export const fetchCourse = createAction<any>(FETCH_COURSE_REQUEST);
const fetchingCourseSucceeded = createAction<FetchCourseResponse>(
  FETCHING_COURSE_SUCCEEDED,
);
const fetchingCourseFailed = createAction<FetchCourseErrorResponse>(
  FETCHING_COURSE_FAILED,
);

const fetchCourseSaga = function* (
  action: ReturnType<typeof fetchCourse>,
): any {
  const id = action.payload;

  const url = buildUrl(apiUrl, {
    path: `api/1.0/courses/${id}`,
  });

  const fetchCourseApi = () => {
    return axios({
      method: "GET",
      url: url,
    }).then((response) => response.data);
  };

  try {
    const response = yield call(fetchCourseApi);
    yield put(
      fetchingCourseSucceeded({
        response: response,
      }),
    );
  } catch (error) {
    yield put(fetchingCourseFailed({ id: id, error: error }));
  }
};

// fetch draft course
export const fetchDraftCourse = createAction<string>(
  FETCH_DRAFT_COURSE_REQUEST,
);
const fetchingDraftCourseSucceeded = createAction<FetchCourseResponse>(
  FETCHING_DRAFT_COURSE_SUCCEEDED,
);
const fetchingDraftCourseFailed = createAction<FetchCourseErrorResponse>(
  FETCHING_DRAFT_COURSE_FAILED,
);

const fetchDraftCourseSaga = function* (
  action: ReturnType<typeof fetchDraftCourse>,
): any {
  const id = action.payload;

  const url = buildUrl(apiUrl, {
    path: `api/1.0/courses/${id}/draft`,
  });

  const fetchCourseApi = () => {
    return axios({
      method: "GET",
      url: url,
    }).then((response) => response.data);
  };

  try {
    const response = yield call(fetchCourseApi);
    yield put(
      fetchingDraftCourseSucceeded({
        response: response,
      }),
    );
  } catch (error) {
    yield put(fetchingDraftCourseFailed({ id: id, error: error }));
  }
};

export const invalidateDraftCourse = createAction<string>(INVALIDATE_DRAFT_COURSE);

// update course
export const updateCourse = createAction<UpdateCourseRequest>(
  UPDATING_COURSE_REQUEST,
);
const updatingCourseSucceeded = createAction<UpdateCourseResponse>(
  UPDATING_COURSE_SUCCEEDED,
);
const updatingCourseFailed = createAction<UpdateCourseErrorResponse>(
  UPDATING_COURSE_FAILED,
);

const updateCourseSaga = function* (
  action: ReturnType<typeof updateCourse>,
): any {
  const { id, request } = action.payload;

  const url = buildUrl(apiUrl, {
    path: `api/1.0/courses/${id}`,
  });

  const updateCourseApi = () => {
    return axios({
      method: "PUT",
      url: url,
      data: request,
    }).then((response) => response.data);
  };

  try {
    const response = yield call(updateCourseApi);
    yield put(updatingCourseSucceeded({ response: response }));
  } catch (error) {
    yield put(updatingCourseFailed({ id: id, error: error }));
  }
};

// delete course
export const deleteCourse = createAction<string>(DELETE_COURSE_REQUEST);
const deletingCourseSucceeded = createAction<DeleteCourseResponse>(DELETING_COURSE_SUCCEEDED);
const deletingCourseFailed = createAction<DeleteCourseErrorResponse>(DELETING_COURSE_FAILED);

const deleteCourseSaga = function* (action: ReturnType<typeof deleteCourse>): any {
  const id = action.payload;

  const url = buildUrl(apiUrl, {
    path: `api/1.0/courses/${id}`,
  });

  const deleteCourseApi = () => {
    return axios({
      method: "DELETE",
      url: url,
    }).then((response) => response.data);
  };

  try {
    const response = yield call(deleteCourseApi);
    yield put(deletingCourseSucceeded({ id: id, response: response }));
  } catch (error) {
    yield put(deletingCourseFailed({ id: id, error: error }));
  }
};

// discard course draft
export const discardCourseDraft = createAction<string>(
  DISCARD_COURSE_DRAFT_REQUEST,
);
const discardingCourseDraftSucceeded = createAction<DiscardCourseDraftResponse>(
  DISCARDING_COURSE_DRAFT_SUCCEEDED,
);
const discardingCourseDraftFailed =
  createAction<DiscardCourseDraftErrorResponse>(DISCARDING_COURSE_DRAFT_FAILED);

const discardCourseDraftSaga = function* (
  action: ReturnType<typeof discardCourseDraft>,
): any {
  const id = action.payload;

  const url = buildUrl(apiUrl, {
    path: `api/1.0/courses/${id}/draft`,
  });

  const discardCourseApi = () => {
    return axios({
      method: "DELETE",
      url: url,
    }).then((response) => response.data);
  };

  try {
    const response = yield call(discardCourseApi);
    yield put(discardingCourseDraftSucceeded({ response: response }));
  } catch (error) {
    yield put(discardingCourseDraftFailed({ id: id, error: error }));
  }
};

// publish course
export const publishCourse = createAction<string>(PUBLISH_COURSE_REQUEST);
const publishingCourseSucceeded = createAction<PublishCourseResponse>(
  PUBLISHING_COURSE_SUCCEEDED,
);
const publishingCourseFailed = createAction<PublishCourseErrorResponse>(
  PUBLISHING_COURSE_FAILED,
);

const publishCourseSaga = function* (
  action: ReturnType<typeof publishCourse>,
): any {
  const id = action.payload;

  const url = buildUrl(apiUrl, {
    path: `api/1.0/courses/${id}/publish`,
  });

  const publishCourseApi = () => {
    return axios({
      method: "POST",
      url: url,
    }).then((response) => response.data);
  };

  try {
    const response = yield call(publishCourseApi);
    yield put(publishingCourseSucceeded({ response: response }));
  } catch (error) {
    yield put(publishingCourseFailed({ id: id, error: error }));
  }
};

export const invalidatePublishCourse = createAction(INVALIDATE_PUBLISH_COURSE);

export const editCourse = createAction<Course>(EDIT_COURSE_REQUEST);

export const validateCourse = createAction<string>(VALIDATE_COURSE_REQUEST);
export const updateValidationErrorsForCourse =
  createAction<UpdateValidationErrorsForCourseRequest>(
    UPDATE_VALIDATION_ERRORS_FOR_COURSE,
  );

const validateCourseSaga = function* (
  action: ReturnType<typeof validateCourse>,
): any {
  const id = action.payload;
  const course = yield select((state) => state.courses.edit?.item);

  if (course && course.id === id) {
    const errors = validateCourseRequest(course);
    yield put(
      updateValidationErrorsForCourse({ course: course.id!, errors: errors }),
    );
  }
};

export const addContentToCourse =
  createAction<AddContentToCourseRequest>(ADD_CONTENT_TO_COURSE);
export const removeEntityFromCourse =
  createAction<RemoveEntityFromCourseRequest>(REMOVE_ENTITY_FROM_COURSE);
export const rearrangeEntitiesInCourse =
  createAction<RearrangeEntitiesInCourseRequest>(REARRANGE_ENTITIES_IN_COURSE);

export const updateCourseProperty = createAction<UpdateCoursePropertyRequest>(
  UPDATE_COURSE_PROPERTY_REQUEST,
);
export const updateLessonProperty = createAction<UpdateLessonPropertyRequest>(
  UPDATE_LESSON_PROPERTY_REQUEST,
);

// create assessment
export const createAssessment = createAction<CreateAssessmentRequest>(
  CREATE_ASSESSMENT_REQUEST,
);
export const updateAssessmentProperty =
  createAction<UpdateAssessmentPropertyRequest>(
    UPDATE_ASSESSMENT_PROPERTY_REQUEST,
  );

export const createQuestionInAssessment =
  createAction<CreateQuestionInAssessmentRequest>(
    CREATE_QUESTION_IN_ASSESSMENT_REQUEST,
  );
export const updateQuestionInAssessment =
  createAction<UpdateQuestionInAssessmentRequest>(
    UPDATE_QUESTION_IN_ASSESSMENT_REQUEST,
  );
export const deleteQuestionInAssessment =
  createAction<DeleteQuestionInAssessmentRequest>(
    DELETE_QUESTION_IN_ASSESSMENT_REQUEST,
  );
export const rearrangeQuestionsInAssessment =
  createAction<RearrangeQuestionsInAssessmentRequest>(
    REARRANGE_QUESTIONS_IN_ASSESSMENT_REQUEST,
  );

export const invalidateCourses = createAction<void>(INVALIDATE_COURSES);
export const invalidateCourseCreationState = createAction<void>(INVALIDATE_COURSE_CREATION_STATE);
export const invalidateCourseUpdateState = createAction<void>(
  INVALIDATE_COURSE_UPDATE_STATE,
);
export const invalidateCourseDeletionState = createAction<string>(
  INVALIDATE_COURSE_DELETION_STATE,
);
export const deleteCourseContent = createAction<any>(DELETE_COURSE_CONTENT);

// generate questions
export const generateQuestions = createAction<GenerateQuestionsRequest>(GENERATE_QUESTIONS_REQUEST);
export const generateQuestionsSuccedded = createAction<GenerateQuestionsResponse>(GENERATING_QUESTIONS_SUCCEEDED);
export const generateQuestionsFailed = createAction<GenerateQuestionsErrorResponse>(GENERATING_QUESTIONS_FAILED);

const generateQuestionsSaga = function* (action: ReturnType<typeof generateQuestions>): any {
  const {course, questions, assets} = action.payload as GenerateQuestionsRequest;

  const url = buildUrl(apiUrl, {
    path: `/api/1.0/seek/questions`
  });

  const data = {
    questions: questions,
    assets: assets
  }

  const generateQuestionsAPI = () => {
    return axios({
      method: 'POST',
      url: url,
      data: data
    }).then((response) => response.data);
  };

  try {
    const response = yield call(generateQuestionsAPI);
    yield put(generateQuestionsSuccedded({
      course: course,
      responses: response
    }));
  } catch (error) {
    yield put(generateQuestionsFailed({
      course: course,
      error: error
    }));
  }
}

export const invalidateGenerateQuestions =  createAction<string>(INVALIDATE_GENERATE_QUESTIONS);

// fetch lesson for course
export const fetchLessonForCourse = createAction<FetchLessonForCourseRequest>(FETCH_LESSON_FOR_COURSE_REQUEST);
const fetchingLessonForCourseSucceeded = createAction<FetchLessonForCourseResponse>(FETCHING_LESSON_FOR_COURSE_SUCCEEDED);
const fetchingLessonForCourseFailed = createAction<FetchLessonForCourseErrorResponse>(FETCHING_LESSON_FOR_COURSE_FAILED);

const fetchLessonForCourseSaga = function* (action: ReturnType<typeof fetchLessonForCourse>): any {
  const { course, lesson, asset } = action.payload as FetchLessonForCourseRequest;

  const url = buildUrl(apiUrl, {
    path: `/api/1.0/courses/${course}/lessons`,
    queryParams: {
      lesson: lesson,
      asset: asset
    }
  });

  const fetchLessonForCourseApi = () => {
    return axios({
      method: 'GET',
      url: url,
    }).then((response) => response.data);
  };

  try {
    const response = yield call(fetchLessonForCourseApi);
    yield put(fetchingLessonForCourseSucceeded({
      course: course,
      lesson: lesson,
      asset: response
    }));
  } catch (error) {
    yield put(fetchingLessonForCourseFailed({
      course: course,
      lesson: lesson,
      asset: asset,
      error: error
    }));
  }
}

// fetch lesson preview for course
export const fetchLessonPreviewForCourse = createAction<FetchLessonPreviewForCourseRequest>(FETCH_LESSON_PREVIEW_FOR_COURSE_REQUEST);
const fetchingLessonPreviewForCourseSucceeded = createAction<FetchLessonPreviewForCourseResponse>(FETCHING_LESSON_PREVIEW_FOR_COURSE_SUCCEEDED);
const fetchingLessonPreviewForCourseFailed = createAction<FetchLessonPreviewForCourseErrorResponse>(FETCHING_LESSON_PREVIEW_FOR_COURSE_FAILED);

const fetchLessonPreviewForCourseSaga = function* (action: ReturnType<typeof fetchLessonForCourse>): any {
  const { course, lesson, asset, includeText } = action.payload as FetchLessonPreviewForCourseRequest;

  const url = buildUrl(apiUrl, {
    path: `/api/1.0/courses/${course}/lessons/preview`,
    queryParams: {
      lesson: lesson,
      asset: asset,
      includeText: "" + includeText
    }
  });

  const fetchLessonPreviewForCourseApi = () => {
    return axios({
      method: 'GET',
      url: url,
    }).then((response) => response.data);
  };

  try {
    const response = yield call(fetchLessonPreviewForCourseApi);
    yield put(fetchingLessonPreviewForCourseSucceeded({
      course: course,
      lesson: lesson,
      asset: asset,
      preview: response
    }));
  } catch (error) {
    yield put(fetchingLessonPreviewForCourseFailed({
      course: course,
      lesson: lesson,
      asset: asset,
      error: error
    }));
  }
}

//fetch attempts for course

export const fetchAttemptsForCourse = createAction<FetchAttemptsForCourseRequest>(FETCH_ATTEMPTS_FOR_COURSE_REQUEST);

const fetchAttemptsForCourseSucceeded = createAction<FetchAttemptsForCourseResponse>(FETCHING_ATTEMPTS_FOR_COURSE_SUCCEEDED);
const fetchAttemptsForCourseFailed = createAction<FetchAttemptsForCourseErrorResponse>(FETCHING_ATTEMPTS_FOR_COURSE_FAILED);

const fetchAttemptsForCourseSaga = function* (action: ReturnType<typeof fetchAttemptsForCourse>): any {
  const {id , learnerId} = action.payload as FetchAttemptsForCourseRequest;

  const url = buildUrl(apiUrl, {
    path: `api/1.0/courses/${id}/attempts`,
  });

  const fetchAttemptsForCourseApi = () => {
    return axios({
      method: "POST",
      url: url,
      data: { learner: learnerId }
    }).then((response) => response.data);
  };

  try {
    const response = yield call(fetchAttemptsForCourseApi);
    yield put(
      fetchAttemptsForCourseSucceeded({response }),
    );
  } catch (error) {
    yield put(fetchAttemptsForCourseFailed({error }));
  }
};

// fetch course 
export const fetchCourseLearnersRecord = createAction<string>(FETCH_COURSE_LEARNING_REQUEST);
const fetchingCourseLearnersRecordSucceeded = createAction<FetchCourseLearningRecordResponse>(FETCHING_COURSE_LEARNING_RECORD_SUCCEEDED);
const fetchingCourseLearnersRecordFailed = createAction<FetchCourseLearningRecordErrorResponse>(FETCHING_COURSE_LEARNING_RECORD_FAILED);

const fetchLearningRecordCourseSaga = function* (action: ReturnType<typeof fetchCourseLearnersRecord>): any {
  const id = action.payload;

  const url = buildUrl(apiUrl, {
    path: `api/1.0/courses/learner/${id}`,
  });

  const fetchCourseLearnersRecordApi = () => {
    return axios({
      method: "GET",
      url: url,
    }).then((response) => response.data);
  };

  try {
    const response = yield call(fetchCourseLearnersRecordApi);
    yield put(
      fetchingCourseLearnersRecordSucceeded({
        response: response,
      }),
    );
  } catch (error) {
    console.log(error);
    yield put(fetchingCourseLearnersRecordFailed({ id: id, error: error }));
  }
};

// fetch assessment report
export const fetchLearnersAssessmentReport = createAction<FetchLearnersAssessmentReportRequest>(FETCH_LEARNERS_ASSESSMENT_REPORT_REQUEST);
const fetchingLearnersAssessmentReportSucceeded = createAction<FetchLearnersAssessmentReportResponse>(FETCHING_LEARNERS_ASSESSMENT_REPORT_SUCCEEDED);
const fetchingLearnersAssessmentReportFailed = createAction<FetchLearnersAssessmentReportErrorResponse>(FETCHING_LEARNERS_ASSESSMENT_REPORT_FAILED);

const fetchLearnersAssessmentReportSaga = function* (action: ReturnType<typeof fetchLearnersAssessmentReport>): any {
  const event = action.payload as FetchLearnersAssessmentReportRequest;

  const url = buildUrl(apiUrl, {
    path: `api/1.0/courses/${event.session}/report`,
  });

  const learnersRecordReportAPI = () => {
    return axios({
      method: "GET",
      url: url,
    }).then((response) => response.data);
  };

  try {
    const response = yield call(learnersRecordReportAPI);
    yield put(fetchingLearnersAssessmentReportSucceeded({
      course: event.course,
      learningRecord: event.learnerRecordId,
      response: response,
    }));
  } catch (error) {
    yield put(fetchingLearnersAssessmentReportFailed({
      course: event.course,
      learningRecord: event.learnerRecordId,
      id: event.id,
      type: event.type,
      error: error
    }));
  }
};

export const invalidateLearnersAssesmentReport = createAction<{
  learningRecord: string;
  sessionId: string;
}>(INVALIDATE_LEARNERS_ASSESSMENT_REPORT);

// sections 
export const createCourseSection = createAction<CreateCourseSectionRequest>(CREATE_COURSE_SECTION_REQUEST,);
export const updateCourseSectionProperty = createAction<UpdateCourseSectionPropertyRequest>(UPDATE_COURSE_SECTION_PROPERTY_REQUEST);


export const searchCoursesSSESubscribe = createAction<any>(SEARCH_COURSES_SSE_SUBSCRIBE);
const searchCoursesSSEMessageReceived = createAction<any>(SEARCH_COURSES_SSE_MESSAGE_RECEIVED);
const searchCoursesSSEError = createAction<any>(SEARCH_COURSES_SSE_ERROR);

function createSSEChannel<T>(url: string, headers: Record<string, string>): EventChannel<any> {
  return eventChannel<any>((emit) => {
    const es = new SSE(url, {
      headers: headers
    });

    es.onopen = () => {
      console.log('SSE (sse.js) connection opened');
    };

    // listen to default messages
    es.addEventListener('agent-text', (e: any) => {
      try {
        const data = {
          type: "agent-text",
          response: e.data
        }
        emit(data);
      } catch (err) {
        console.error('SSE parse error', err);
      }
    });

    //listen to named events from Spring SseEmitter
    es.addEventListener('agent-response', (e: any) => {
      try {
        const data = {
          type: "agent-response",
          response: JSON.parse(e.data),
        }
        emit(data);
      } catch (err) {
        console.error('SSE named event parse error', err);
      }
    });

    es.onerror = (err: any) => {
      console.error('SSE (sse.js) error', err);
      emit(new Error('SSE error'));
      // we could optionally send END
      // emit(END);
    };

    // unsubscribe function
    return () => {
      console.log('Closing SSE (sse.js)');
     es.close();
    };
  });
}

const searchCoursesSSESubscribeSaga = function* (action: ReturnType<typeof searchCoursesSSESubscribe>): any {
  const data = action.payload;
  const params = {
    queryParams: {
      query: data.query,
      session: data.session,
      page: data.page,
      limit: data.limit,
    }
  };

  const url = buildUrl(`${searchApiUrl}/coaching/3.0/agent/search`, params);

  // construct headers
  const token = getAccessToken();
  const headers = {
    Authorization: `Bearer ${token}`,
    'Accept': 'text/event-stream'
  };

  const channel: Channel<any> = yield call(createSSEChannel, url, headers);

  try {
    while(true) {
      const data = yield take(channel);
      if(data.type === "agent-text") {
        yield put(searchCoursesSSEMessageReceived({ type: "agent-text", response: data.response }));
      }
      yield put(searchCoursesSSEMessageReceived({ type: "agent-response", response: data.response }));
    }

  } catch (error) {
    yield put(searchCoursesSSEError({ error: error }));

  }
};

export const invalidateSSESearchResults = createAction<void>(INVALIDATE_SSE_SEARCH_RESULTS);

//fetch published simulation
export const fetchPublishedSimulations = createAction<FetchPublishedCoursesRequest>(FETCH_PUBLISHED_SIMULATIONS_REQUEST);  //simulations
const fetchPublishedSimulationsSucceeded = createAction<FetchPublishedCoursesResponse>(FETCHING_PUBLISHED_SIMULATIONS_SUCCEEDED);
const fetchPublishedSimulationsFailed = createAction<FetchPublishedCoursesErrorResponse>(FETCHING_PUBLISHED_SIMULATIONS_FAILED);  //seperate the types for simulation and podcasts

const fetchPublishedSimulationsSaga = function* (
  action: ReturnType<typeof fetchPublishedSimulations>
):any {
  const request = action.payload as FetchPublishedCoursesRequest;
    const url = buildUrl(`${apiUrl}/api/1.0/courses/published`, {
    queryParams: {
      page: request.page,
      limit: request.limit,
      courseTypes: ["simulation"]
    }
  });

  const fetchPublishedSimulationApi = () => {
    return axios({
      method: "GET",
      url: url
    }).then((response) => response.data);
  };

  try {
    const response = yield call(fetchPublishedSimulationApi);
    yield put(
      fetchPublishedSimulationsSucceeded({
        response: response,
      }),
    );
  } catch (error) {
    yield put(fetchPublishedSimulationsFailed({ error: error }));
  }
}

//search published simulation
export const searchPublishedSimulations = createAction<SearchPublishedSimulationsRequest>(SEARCH_PUBLISHED_SIMULATIONS_REQUEST);
const searchPublishedSimulationsSucceeded = createAction<SearchPublishedSimulationsResponse>(SEARCHING_PUBLISHED_SIMULATIONS_SUCCEEDED);
const searchPublishedSimulationsFailed = createAction<SearchPublishedSimulationsErrorResponse>(SEARCHING_PUBLISHED_SIMULATIONS_FAILED);

const searchPublishedSimulationsSaga = function* (
  action: ReturnType<typeof searchPublishedSimulations>
):any {
  const request = action.payload as SearchPublishedSimulationsRequest;
    const url = buildUrl(`${apiUrl}/api/1.0/courses/published`, {
    queryParams: {
      page: request.page,
      limit: request.limit,
      courseTypes: ["simulation"],
      query: request.query
    }
  });

  const searchPublishedSimulationApi = () => {
    return axios({
      method: "GET",
      url: url
    }).then((response) => response.data);
  };

  try {
    const response = yield call(searchPublishedSimulationApi);
    yield put(
      searchPublishedSimulationsSucceeded({
        response: response,
      }),
    );
  } catch (error) {
    yield put(searchPublishedSimulationsFailed({ error: error }));
  }
}

//fetch published podcast
export const fetchPublishedPodcasts = createAction<FetchPublishedCoursesRequest>(FETCH_PUBLISHED_PODCASTS_REQUEST);
const fetchPublishedPodcastsSucceeded = createAction<FetchPublishedCoursesResponse>(FETCHING_PUBLISHED_PODCASTS_SUCCEEDED);
const fetchPublishedPodcastsFailed = createAction<FetchPublishedCoursesErrorResponse>(FETCHING_PUBLISHED_PODCASTS_FAILED);

const fetchPublishedPodcastsSaga = function* (
  action: ReturnType<typeof fetchPublishedPodcasts>
):any {
  const request = action.payload as FetchPublishedCoursesRequest;
    const url = buildUrl(`${apiUrl}/api/1.0/courses/published`, {
    queryParams: {
      page: request.page,
      limit: request.limit,
      courseTypes: ["podcast"]
    }
  });

  const fetchPublishedPodcastApi = () => {
    return axios({
      method: "GET",
      url: url
    }).then((response) => response.data);
  };

  try {
    const response = yield call(fetchPublishedPodcastApi);
    yield put(
      fetchPublishedPodcastsSucceeded({
        response: response,
      }),
    );
  } catch (error) {
    yield put(fetchPublishedPodcastsFailed({ error: error }));
  }
}

//search published podcast
export const searchPublishedPodcasts = createAction<SearchPublishedPodcastsRequest>(SEARCH_PUBLISHED_PODCASTS_REQUEST);
const searchPublishedPodcastsSucceeded = createAction<SearchPublishedPodcastsResponse>(SEARCHING_PUBLISHED_PODCASTS_SUCCEEDED);
const searchPublishedPodcastsFailed = createAction<SearchPublishedPodcastsErrorResponse>(SEARCHING_PUBLISHED_PODCASTS_FAILED);

const searchPublishedPodcastsSaga = function* (
  action: ReturnType<typeof searchPublishedPodcasts>
):any {
  const request = action.payload as SearchPublishedPodcastsRequest;
    const url = buildUrl(`${apiUrl}/api/1.0/courses/published`, {
    queryParams: {
      page: request.page,
      limit: request.limit,
      courseTypes: ["podcast"],
      query: request.query
    }
  });

  const searchPublishedPodcastApi = () => {
    return axios({
      method: "GET",
      url: url
    }).then((response) => response.data);
  };

  try {
    const response = yield call(searchPublishedPodcastApi);
    yield put(
      searchPublishedPodcastsSucceeded({
        response: response,
      }),
    );
  } catch (error) {
    yield put(searchPublishedPodcastsFailed({ error: error }));
  }
}

export const invalidatePublishedPodcasts = createAction<void>(INVALIDATE_PUBLISHED_PODCASTS);

export const invalidatePublishedSimulations = createAction<void>(INVALIDATE_PUBLISHED_SIMULATIONS);


export default function* coursesSaga() {
  yield takeEvery(FETCHING_DRAFT_COURSES_REQUEST, fetchDraftCoursesSaga);
  yield takeEvery(FETCHING_PUBLISHED_COURSES_REQUEST,fetchPublishedCoursesSaga);
  yield takeEvery(UPDATING_COURSE_REQUEST, updateCourseSaga);
  yield takeEvery(VALIDATE_COURSE_REQUEST, validateCourseSaga);
  yield takeEvery(DELETE_COURSE_REQUEST, deleteCourseSaga);
  yield takeEvery(DISCARD_COURSE_DRAFT_REQUEST, discardCourseDraftSaga);
  yield takeEvery(FETCH_COURSE_REQUEST, fetchCourseSaga);
  yield takeEvery(FETCH_DRAFT_COURSE_REQUEST, fetchDraftCourseSaga);
  yield takeEvery(CREATE_COURSE_REQUEST, createCourseSaga);
  yield takeEvery(PUBLISH_COURSE_REQUEST, publishCourseSaga);
  yield takeEvery(GENERATE_QUESTIONS_REQUEST, generateQuestionsSaga);
  yield takeEvery(FETCH_LESSON_FOR_COURSE_REQUEST, fetchLessonForCourseSaga);
  yield takeEvery(FETCH_LESSON_PREVIEW_FOR_COURSE_REQUEST, fetchLessonPreviewForCourseSaga);
  yield takeEvery(FETCH_ATTEMPTS_FOR_COURSE_REQUEST, fetchAttemptsForCourseSaga);
  yield takeEvery(FETCH_COURSE_LEARNING_REQUEST, fetchLearningRecordCourseSaga);
  yield takeEvery(FETCH_LEARNERS_ASSESSMENT_REPORT_REQUEST, fetchLearnersAssessmentReportSaga);
  yield takeEvery(SEARCH_COURSES_SSE_SUBSCRIBE, searchCoursesSSESubscribeSaga);
  yield takeEvery(FETCH_PUBLISHED_SIMULATIONS_REQUEST, fetchPublishedSimulationsSaga);
  yield takeEvery(FETCH_PUBLISHED_PODCASTS_REQUEST, fetchPublishedPodcastsSaga);
  yield takeEvery(SEARCH_PUBLISHED_SIMULATIONS_REQUEST, searchPublishedSimulationsSaga);
  yield takeEvery(SEARCH_PUBLISHED_PODCASTS_REQUEST, searchPublishedPodcastsSaga);
}
